import { supabase } from "@/lib/supabase";
import { toCsv } from "@/lib/csv";
import { linkSupplierProduct } from "@/lib/api/supplier-products";
import { upsertAttribute, applyCanonicalProductData, upsertCanonicalTradeIdentifiers } from "@/lib/api/products";
import { parseCanonicalImportFields } from "@/lib/catalogue-facts";
import { formatImportFieldError, normalizeImportAttributeName } from "@/lib/catalogue-identifiers";
import { validateIdentifierValue } from "@/lib/products";
import { getActiveCurrencyCode } from "@/lib/currency";

export type RowAction = "create" | "update" | "skip";

export interface ParsedRow {
  sourceLine: number;
  label: string;
  action: RowAction;
  updateId?: string;
  payload: Record<string, unknown>;
  warnings: string[];
  errors: string[];
  conflicts: string[];
  matchNote?: string;
}

export interface ImportIssue {
  label: string;
  error: string;
}

export interface ImportSummary {
  created: number;
  updated: number;
  skipped: number;
  /** Products created/updated atomically but with optional follow-ups (attributes/supplier) that failed to apply. */
  partial: number;
  failed: ImportIssue[];
  needsAttention: ImportIssue[];
}

export const headerIndex = (headers: string[], name: string): number =>
  headers.findIndex((h) => h.trim().toLowerCase() === name.trim().toLowerCase());

export const cell = (row: string[], idx: number): string => (idx >= 0 ? (row[idx] ?? "").trim() : "");

export const isNum = (v: string): boolean => v !== "" && !Number.isNaN(Number(v));

// ---------------------------------------------------------------------------
// Generic executor for flat tables (customers, suppliers): batch insert the
// "create" rows, individually update the "update" rows, skip the rest. Returns
// an honest summary with row-level failures.
// ---------------------------------------------------------------------------
export async function executeImport(
  table: "customers" | "suppliers",
  orgId: string,
  rows: ParsedRow[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportSummary> {
  const creates = rows.filter((r) => r.action === "create" && r.errors.length === 0);
  const updates = rows.filter((r) => r.action === "update" && r.updateId && r.errors.length === 0);
  const skipped = rows.filter((r) => r.action === "skip" || r.errors.length > 0).length;

  const summary: ImportSummary = { created: 0, updated: 0, skipped, partial: 0, failed: [], needsAttention: [] };
  const totalOps = creates.length + updates.length;
  let doneOps = 0;
  const tick = () => {
    doneOps += 1;
    onProgress?.(doneOps, totalOps);
  };

  const CHUNK = 200;
  for (let i = 0; i < creates.length; i += CHUNK) {
    const chunk = creates.slice(i, i + CHUNK);
    const { error } = await supabase.from(table).insert(chunk.map((r) => ({ ...r.payload, org_id: orgId })));
    if (!error) {
      summary.created += chunk.length;
      chunk.forEach(() => tick());
    } else {
      // Fall back to per-row inserts to report exactly which rows failed.
      for (const r of chunk) {
        const { error: rowErr } = await supabase.from(table).insert({ ...r.payload, org_id: orgId });
        if (rowErr) summary.failed.push({ label: r.label, error: rowErr.message });
        else summary.created += 1;
        tick();
      }
    }
  }

  await Promise.all(
    updates.map(async (r) => {
      const { error } = await supabase.from(table).update(r.payload).eq("id", r.updateId!);
      if (error) summary.failed.push({ label: r.label, error: error.message });
      else summary.updated += 1;
      tick();
    }),
  );

  return summary;
}

// ---------------------------------------------------------------------------
// Products + variants executor. Product rows are created/updated first, then
// variant rows (which must reference an existing parent product) are inserted.
// ---------------------------------------------------------------------------
async function applyAttributes(orgId: string, productId: string, attributes: Record<string, string>): Promise<string[]> {
  const errors: string[] = [];
  for (const [name, value] of Object.entries(attributes ?? {})) {
    if (!value) continue;
    const attrName = normalizeImportAttributeName(name);
    let attrId: string | undefined;
    const { data: existing, error: lookErr } = await supabase.from("product_attributes").select("id").eq("org_id", orgId).ilike("name", attrName).maybeSingle();
    if (lookErr) {
      errors.push(`attribute "${attrName}": ${lookErr.message}`);
      continue;
    }
    attrId = (existing as { id?: string } | null)?.id;
    if (!attrId) {
      try {
        attrId = await upsertAttribute({ orgId, name: attrName, dataType: "text", options: [], required: false, units: null, categoryId: null, position: 0 });
      } catch (e) {
        errors.push(`attribute "${attrName}": ${(e as Error).message}`);
        continue;
      }
    }
    if (!attrId) {
      errors.push(`attribute "${attrName}" could not be created`);
      continue;
    }
    const { error: upsertErr } = await supabase.from("product_attribute_values").upsert(
      { org_id: orgId, product_id: productId, attribute_id: attrId, value },
      { onConflict: "org_id,product_id,attribute_id" },
    );
    if (upsertErr) errors.push(`attribute "${attrName}": ${upsertErr.message}`);
  }
  return errors;
}

async function applySupplierLink(orgId: string, productId: string, supplier: Record<string, unknown>): Promise<string[]> {
  const errors: string[] = [];
  const name = String(supplier.supplier_name ?? "").trim();
  if (!name) return errors;
  const { data, error: lookErr } = await supabase.from("suppliers").select("id").eq("org_id", orgId).ilike("name", name).maybeSingle();
  if (lookErr) {
    errors.push(`supplier "${name}": ${lookErr.message}`);
    return errors;
  }
  const supplierId = (data as { id?: string } | null)?.id;
  if (!supplierId) {
    errors.push(`supplier "${name}" not found — link not created`);
    return errors;
  }
  const existing = await supabase.from("supplier_products").select("id").eq("org_id", orgId).eq("product_id", productId).eq("supplier_id", supplierId).is("variant_id", null).maybeSingle();
  if (existing.error) {
    errors.push(`supplier "${name}": ${existing.error.message}`);
    return errors;
  }
  if ((existing.data as { id?: string } | null)?.id) return errors;
  // Canonical single write path shared with the Product/Supplier editors.
  try {
    await linkSupplierProduct(orgId, {
      productId,
      supplierId,
      variantId: null,
      supplierSku: (supplier.supplier_sku as string) || null,
      supplierPrice: (supplier.supplier_cost as number) ?? null,
      minOrderQty: (supplier.min_order_qty as number) ?? 1,
      orderMultiple: (supplier.order_multiple as number) ?? null,
      leadTimeDays: (supplier.lead_time_days as number) ?? null,
      isPreferred: (supplier.is_preferred as boolean) ?? false,
    });
  } catch (e) {
    errors.push(`supplier "${name}": ${(e as Error).message}`);
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Helpers for routing a logical imported product through the trusted
// create_product RPC: one product + its option/variant structure in a single
// atomic backend transaction.
// ---------------------------------------------------------------------------

function deriveOptions(variants: ParsedRow[]): Array<{ name: string; position: number; values: { value: string; position: number }[] }> {
  const byName = new Map<string, Map<string, string>>();
  for (const v of variants) {
    const ov = (v.payload as Record<string, unknown>).option_values as Record<string, string> | undefined;
    if (!ov) continue;
    for (const [name, value] of Object.entries(ov)) {
      if (!value) continue;
      const lv = value.toLowerCase();
      if (!byName.has(name)) byName.set(name, new Map());
      if (!byName.get(name)!.has(lv)) byName.get(name)!.set(lv, value);
    }
  }
  const options: Array<{ name: string; position: number; values: { value: string; position: number }[] }> = [];
  let pos = 0;
  for (const [name, values] of byName) {
    options.push({ name, position: pos++, values: Array.from(values.values()).map((value, i) => ({ value, position: i })) });
  }
  return options;
}

function buildVariantPayload(p: Record<string, unknown>): Record<string, unknown> {
  return {
    sku: (p.sku as string | null) ?? null,
    barcode: (p.barcode as string | null) ?? null,
    cost_price: (p.cost_price as number | null) ?? null,
    selling_price: (p.selling_price as number | null) ?? null,
    position: 0,
    status: "active",
    option_values: (p.option_values as Record<string, string>) ?? {},
  };
}

function naturalKey(sku: string | null, barcode: string | null, reference: string | null): string | null {
  if (sku) return `csv-sku:${sku.trim().toLowerCase()}`;
  if (barcode) return `csv-barcode:${barcode.trim().toLowerCase()}`;
  if (reference) return `csv-ref:${reference.trim().toLowerCase()}`;
  return null;
}

async function applyImportedTradeIdentifiers(
  orgId: string,
  productId: string,
  variantId: string | null,
  payload: Record<string, unknown>,
): Promise<string[]> {
  const gtin = variantId ? (payload.variant_gtin as string | null) : (payload.gtin as string | null);
  const upc = variantId ? (payload.variant_upc as string | null) : (payload.upc as string | null);
  const ean = variantId ? (payload.variant_ean as string | null) : (payload.ean as string | null);
  if (!gtin && !upc && !ean) return [];
  try {
    await upsertCanonicalTradeIdentifiers(orgId, productId, variantId, {
      gtin: gtin ?? null,
      upc: upc ?? null,
      ean: ean ?? null,
      identifiersNotApplicable: variantId ? false : Boolean(payload.identifiers_not_applicable),
    });
    return [];
  } catch (e) {
    return [(e as Error).message];
  }
}

export async function executeProductsImport(
  orgId: string,
  rows: ParsedRow[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportSummary> {
  const productRows = rows.filter((r) => (r.payload as Record<string, unknown>).__kind !== "variant");
  const variantRows = rows.filter((r) => (r.payload as Record<string, unknown>).__kind === "variant");

  const summary: ImportSummary = { created: 0, updated: 0, skipped: 0, partial: 0, failed: [], needsAttention: [] };
  summary.skipped = rows.filter((r) => r.action === "skip" || r.errors.length > 0).length;

  // Split variant rows: those bound to a product being created in this same
  // import (parent_sku, no parent_id) are folded into create_product; those
  // bound to an existing product (parent_id set) keep the variant-only path.
  const variantsByParentSku = new Map<string, ParsedRow[]>();
  const variantsByParentId = new Map<string, ParsedRow[]>();
  for (const vr of variantRows) {
    if (vr.errors.length > 0 || vr.action === "skip") continue;
    const p = vr.payload as Record<string, unknown>;
    const parentId = p.parent_id as string | null | undefined;
    const parentSku = p.parent_sku as string | null | undefined;
    if (parentId) {
      if (!variantsByParentId.has(parentId)) variantsByParentId.set(parentId, []);
      variantsByParentId.get(parentId)!.push(vr);
    } else if (parentSku) {
      const key = parentSku.toLowerCase();
      if (!variantsByParentSku.has(key)) variantsByParentSku.set(key, []);
      variantsByParentSku.get(key)!.push(vr);
    }
  }

  const creates = productRows.filter((r) => r.action === "create" && r.errors.length === 0);
  const updates = productRows.filter((r) => r.action === "update" && r.updateId && r.errors.length === 0);

  const strip = (payload: Record<string, unknown>): Record<string, unknown> => {
    const {
      __kind: _k,
      __attributes: _a,
      __supplier: _s,
      __touched: touched,
      gtin: _g,
      upc: _u,
      ean: _e,
      variant_gtin: _vg,
      variant_upc: _vu,
      variant_ean: _ve,
      ...rest
    } = payload;
    void _k;
    void _a;
    void _s;
    void _g;
    void _u;
    void _e;
    void _vg;
    void _vu;
    void _ve;
    if (!touched || typeof touched !== "object") return rest;
    const flags = touched as Record<string, boolean>;
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rest)) {
      if (flags[key] === false) continue;
      out[key] = value;
    }
    return out;
  };

  const existingParentVariants = Array.from(variantsByParentId.values()).flat();
  const totalOps = creates.length + updates.length + existingParentVariants.length;
  let doneOps = 0;
  const tick = () => {
    doneOps += 1;
    onProgress?.(doneOps, totalOps);
  };

  // --- Creates: one atomic create_product per logical product ---
  for (const r of creates) {
    tick();
    const p = r.payload as Record<string, unknown>;
    const sku = (p.sku as string | null) ?? null;
    const barcode = (p.barcode as string | null) ?? null;
    const reference = (p.reference as string | null) ?? null;

    const variants = sku ? (variantsByParentSku.get(sku.toLowerCase()) ?? []) : [];
    const variationMode: "simple" | "variation" = variants.some((v) => {
      const ov = (v.payload as Record<string, unknown>).option_values as Record<string, string> | undefined;
      return !!ov && Object.keys(ov).length > 0;
    })
      ? "variation"
      : "simple";

    const { data: created, error } = await supabase.rpc("create_product", {
      p_org_id: orgId,
      p_name: p.name,
      p_reference: reference,
      p_brand: (p.brand as string | null) ?? null,
      p_category_id: (p.category_id as string | null) ?? null,
      p_status: (p.status as string) || "active",
      p_description: (p.description as string | null) ?? null,
      p_short_description: null,
      p_seo_title: null,
      p_meta_description: null,
      p_tags: null,
      p_unit: "each",
      p_product_type: "physical",
      p_variation_mode: variationMode,
      p_sku: sku,
      p_barcode: variationMode === "simple" ? barcode : null,
      p_cost_price: (p.cost_price as number | null) ?? null,
      p_selling_price: (p.selling_price as number | null) ?? null,
      p_tax_profile: null,
      p_weight_kg: (p.weight_kg as number | null) ?? null,
      p_length_cm: (p.length_cm as number | null) ?? null,
      p_width_cm: (p.width_cm as number | null) ?? null,
      p_height_cm: (p.height_cm as number | null) ?? null,
      p_reorder_point: (p.reorder_point as number) || 0,
      p_reorder_qty: (p.reorder_qty as number) || 0,
      p_options: variationMode === "variation" ? deriveOptions(variants) : [],
      p_variants: variationMode === "variation" ? variants.map((v) => buildVariantPayload(v.payload as Record<string, unknown>)) : [],
      p_initial_stock: null,
      p_idempotency_key: naturalKey(sku, barcode, reference),
    });

    if (error) {
      summary.failed.push({ label: r.label, error: error.message });
      continue;
    }

    summary.created += 1;
    const pid = (created as { product_id?: string } | null)?.product_id;

    const attrs = p.__attributes as Record<string, string> | undefined;
    const supplier = p.__supplier as Record<string, unknown> | undefined;
    if (pid) {
      const followUpErrors: string[] = [];
      if (attrs && Object.keys(attrs).length > 0) {
        followUpErrors.push(...(await applyAttributes(orgId, pid, attrs)));
      }
      if (supplier) {
        followUpErrors.push(...(await applySupplierLink(orgId, pid, supplier)));
      }
      try {
        await applyCanonicalProductData(orgId, pid, {
          manufacturer: (p.manufacturer as string | null) ?? null,
          mpn: (p.mpn as string | null) ?? null,
          condition: (p.condition as string | null) ?? null,
          country_of_origin: (p.country_of_origin as string | null) ?? null,
          identifiers_not_applicable: Boolean(p.identifiers_not_applicable),
          gtin: (p.gtin as string | null) ?? null,
          upc: (p.upc as string | null) ?? null,
          ean: (p.ean as string | null) ?? null,
        });
      } catch (e) {
        followUpErrors.push(`canonical data: ${(e as Error).message}`);
      }
      for (const vr of variants) {
        const vp = vr.payload as Record<string, unknown>;
        const vsku = String(vp.sku ?? "").trim();
        if (!vsku) continue;
        const { data: vrow } = await supabase.from("variants").select("id").eq("org_id", orgId).eq("product_id", pid).eq("sku", vsku).maybeSingle();
        const vid = (vrow as { id?: string } | null)?.id;
        if (vid) followUpErrors.push(...(await applyImportedTradeIdentifiers(orgId, pid, vid, vp)));
      }
      if (followUpErrors.length > 0) {
        summary.partial += 1;
        for (const e of followUpErrors) summary.needsAttention.push({ label: r.label, error: e });
      }
    }
  }

  // --- Updates: RLS-guarded scalar edit on the existing product row ---
  for (const r of updates) {
    tick();
    const { error } = await supabase.from("products").update(strip(r.payload)).eq("id", r.updateId!);
    if (error) {
      summary.failed.push({ label: r.label, error: error.message });
      continue;
    }
    summary.updated += 1;
    const p = r.payload as Record<string, unknown>;
    const followUpErrors: string[] = [];
    const attrs = p.__attributes as Record<string, string> | undefined;
    if (attrs && Object.keys(attrs).length > 0 && r.updateId) {
      followUpErrors.push(...(await applyAttributes(orgId, r.updateId, attrs)));
    }
    const supplier = p.__supplier as Record<string, unknown> | undefined;
    if (supplier && r.updateId) {
      followUpErrors.push(...(await applySupplierLink(orgId, r.updateId, supplier)));
    }
    if (r.updateId) {
      const t = (p.__touched as Record<string, boolean> | undefined) ?? {};
      try {
        await applyCanonicalProductData(orgId, r.updateId, {
          manufacturer: t.manufacturer ? ((p.manufacturer as string | null) ?? null) : undefined,
          mpn: t.mpn ? ((p.mpn as string | null) ?? null) : undefined,
          condition: t.condition ? ((p.condition as string | null) ?? null) : undefined,
          country_of_origin: t.country_of_origin ? ((p.country_of_origin as string | null) ?? null) : undefined,
          identifiers_not_applicable: t.identifiers_not_applicable ? Boolean(p.identifiers_not_applicable) : undefined,
          gtin: t.gtin ? ((p.gtin as string | null) ?? null) : undefined,
          upc: t.upc ? ((p.upc as string | null) ?? null) : undefined,
          ean: t.ean ? ((p.ean as string | null) ?? null) : undefined,
        });
      } catch (e) {
        followUpErrors.push(`canonical data: ${(e as Error).message}`);
      }
    }
    if (followUpErrors.length > 0) {
      summary.partial += 1;
      for (const e of followUpErrors) summary.needsAttention.push({ label: r.label, error: e });
    }
  }

  // --- Variants bound to existing products ---
  for (const [, vlist] of variantsByParentId) {
    for (const r of vlist) {
      tick();
      const p = r.payload as Record<string, unknown>;
      const parentId = p.parent_id as string | null;
      if (!parentId) {
        summary.failed.push({ label: r.label, error: "Variant parent product is missing" });
        continue;
      }
      if (r.action === "update" && r.updateId) {
        const identErrs = await applyImportedTradeIdentifiers(orgId, parentId, r.updateId, p);
        if (identErrs.length > 0) {
          summary.partial += 1;
          for (const e of identErrs) summary.needsAttention.push({ label: r.label, error: e });
        } else {
          summary.updated += 1;
        }
        continue;
      }
      const optionValues = p.option_values as Record<string, string> | undefined;
      if (optionValues && Object.keys(optionValues).length > 0) {
        const optionsPayload = Object.entries(optionValues).map(([name, value], pos) => ({
          name,
          position: pos,
          values: [{ value, position: 0 }],
        }));
        const variantPayload = [{
          sku: (p.sku as string | null) ?? null,
          barcode: (p.barcode as string | null) ?? null,
          cost_price: (p.cost_price as number | null) ?? null,
          selling_price: (p.selling_price as number | null) ?? null,
          option_values: optionValues,
          position: 0,
          status: "active",
        }];
        const { error: rpcErr } = await supabase.rpc("create_variant_matrix", {
          p_org_id: orgId,
          p_product_id: parentId,
          p_options: optionsPayload,
          p_variants: variantPayload,
        });
        if (rpcErr) {
          summary.failed.push({ label: r.label, error: rpcErr.message });
          continue;
        }
        summary.created += 1;
        const vsku = String(p.sku ?? "").trim();
        if (vsku) {
          const { data: vrow } = await supabase.from("variants").select("id").eq("org_id", orgId).eq("product_id", parentId).eq("sku", vsku).maybeSingle();
          const vid = (vrow as { id?: string } | null)?.id;
          if (vid) {
            const identErrs = await applyImportedTradeIdentifiers(orgId, parentId, vid, p);
            if (identErrs.length > 0) {
              summary.partial += 1;
              for (const e of identErrs) summary.needsAttention.push({ label: r.label, error: e });
            }
          }
        }
      } else {
        const { data: inserted, error: rowErr } = await supabase.from("variants").insert({
          org_id: orgId,
          product_id: parentId,
          sku: (p.sku as string | null) ?? null,
          barcode: (p.barcode as string | null) ?? null,
          cost_price: (p.cost_price as number | null) ?? null,
          selling_price: (p.selling_price as number | null) ?? null,
        }).select("id").maybeSingle();
        if (rowErr) summary.failed.push({ label: r.label, error: rowErr.message });
        else {
          summary.created += 1;
          const vid = (inserted as { id?: string } | null)?.id;
          if (vid) {
            const identErrs = await applyImportedTradeIdentifiers(orgId, parentId, vid, p);
            if (identErrs.length > 0) {
              summary.partial += 1;
              for (const e of identErrs) summary.needsAttention.push({ label: r.label, error: e });
            }
          }
        }
      }
    }
  }

  return summary;
}

// ---------------------------------------------------------------------------
// Products / variants parse + validation
// ---------------------------------------------------------------------------
export async function parseProductsCsv(rows: string[][], headers: string[], orgId: string): Promise<ParsedRow[]> {
  const nameIdx = headerIndex(headers, "name");
  const refIdx = headerIndex(headers, "reference");
  const skuIdx = headerIndex(headers, "sku");
  const barcodeIdx = headerIndex(headers, "barcode");
  const categoryIdx = headerIndex(headers, "category");
  const costIdx = headerIndex(headers, "cost_price");
  const priceIdx = headerIndex(headers, "selling_price");
  const ropIdx = headerIndex(headers, "reorder_point");
  const roqIdx = headerIndex(headers, "reorder_qty");
  const descIdx = headerIndex(headers, "description");
  const vskuIdx = headerIndex(headers, "variant_sku");
  const vbarIdx = headerIndex(headers, "variant_barcode");
  const vgtinIdx = headerIndex(headers, "variant_gtin");
  const vupcIdx = headerIndex(headers, "variant_upc");
  const veanIdx = headerIndex(headers, "variant_ean");
  const vcostIdx = headerIndex(headers, "variant_cost");
  const vpriceIdx = headerIndex(headers, "variant_price");
  const supplierNameIdx = headerIndex(headers, "supplier_name");
  const supplierSkuIdx = headerIndex(headers, "supplier_sku");
  const supplierCostIdx = headerIndex(headers, "supplier_cost");
  const supplierMoqIdx = headerIndex(headers, "supplier_moq");
  const supplierMultipleIdx = headerIndex(headers, "supplier_order_multiple");
  const supplierLeadIdx = headerIndex(headers, "supplier_lead_time");
  const supplierPreferredIdx = headerIndex(headers, "supplier_preferred");

  // V2 multi-option detection: headers matching "Option:*" e.g. "Option:Colour", "Option:Size", "Option:Gender"
  const optionHeaderIndices: { name: string; idx: number }[] = [];
  headers.forEach((h, idx) => {
    const m = h.match(/^[Oo]ption[:\s](.+)$/);
    if (m) optionHeaderIndices.push({ name: m[1].trim(), idx });
  });

  // Legacy single-option support
  const voptNameIdx = optionHeaderIndices.length === 0 ? headerIndex(headers, "option_name") : -1;
  const voptValIdx = optionHeaderIndices.length === 0 ? headerIndex(headers, "option_value") : -1;

  // Attribute columns: headers matching "Attribute:*" e.g. "Attribute:Material", "Attribute:Pattern"
  const attributeHeaderIndices: { name: string; idx: number }[] = [];
  headers.forEach((h, idx) => {
    const m = h.match(/^[Aa]ttribute[:\s](.+)$/);
    if (m) attributeHeaderIndices.push({ name: m[1].trim(), idx });
  });

  const [catRes, prodRes, varRes, idRes] = await Promise.all([
    supabase.from("categories").select("id, name").eq("org_id", orgId),
    supabase.from("products").select("id, name, sku, barcode, reference").eq("org_id", orgId),
    supabase.from("variants").select("id, product_id, sku, barcode").eq("org_id", orgId),
    supabase.from("product_identifiers").select("id, org_id, product_id, variant_id, type, value").eq("org_id", orgId),
  ]);

  const catByName = new Map<string, string>();
  (catRes.data ?? []).forEach((c: { id: string; name: string }) => catByName.set(c.name.trim().toLowerCase(), c.id));

  const supplierByName = new Map<string, string>();
  const { data: supplierRows } = await supabase.from("suppliers").select("id, name").eq("org_id", orgId);
  (supplierRows ?? []).forEach((s: { id: string; name: string }) => supplierByName.set(s.name.trim().toLowerCase(), s.id));

  const productBySku = new Map<string, { id: string }>();
  const productByBarcode = new Map<string, { id: string }>();
  const productByReference = new Map<string, { id: string }>();
  (prodRes.data ?? []).forEach((p: { id: string; sku: string | null; barcode: string | null; reference: string | null }) => {
    if (p.sku) productBySku.set(p.sku.trim().toLowerCase(), { id: p.id });
    if (p.barcode) productByBarcode.set(p.barcode.trim().toLowerCase(), { id: p.id });
    if (p.reference) productByReference.set(p.reference.trim().toLowerCase(), { id: p.id });
  });
  const variantSkuSet = new Set<string>();
  const variantBarcodeSet = new Set<string>();
  const variantBySku = new Map<string, { id: string; product_id: string }>();
  (varRes.data ?? []).forEach((v: { id: string; product_id: string; sku: string | null; barcode: string | null }) => {
    if (v.sku) {
      variantSkuSet.add(v.sku.trim().toLowerCase());
      variantBySku.set(v.sku.trim().toLowerCase(), { id: v.id, product_id: v.product_id });
    }
    if (v.barcode) variantBarcodeSet.add(v.barcode.trim().toLowerCase());
  });
  const identRows = (idRes.data ?? []) as { id: string; org_id: string; product_id: string | null; variant_id: string | null; type: string; value: string }[];

  // In-file duplicate trackers
  const fileSku = new Map<string, number>();
  const fileBarcode = new Map<string, number>();
  const fileVariantSku = new Map<string, number>();
  const fileVariantBarcode = new Map<string, number>();
  const fileReference = new Map<string, number>();
  const fileProductSkus = new Set<string>();
  const fileTradeIds = new Map<string, number>();

  const parsed: ParsedRow[] = [];

  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const line = i + 1;
    const name = cell(r, nameIdx);
    const reference = cell(r, refIdx);
    const sku = cell(r, skuIdx);
    const barcode = cell(r, barcodeIdx);
    const category = cell(r, categoryIdx);
    const cost = cell(r, costIdx);
    const price = cell(r, priceIdx);
    const rop = cell(r, ropIdx);
    const roq = cell(r, roqIdx);
    const desc = cell(r, descIdx);

    const errors: string[] = [];
    const warnings: string[] = [];
    const canon = parseCanonicalImportFields((h) => cell(r, headerIndex(headers, h)));
    for (const e of canon.errors) {
      const field = e.split(":")[0] ?? "field";
      errors.push(formatImportFieldError(line, field, e));
    }
    for (const type of ["gtin", "upc", "ean"] as const) {
      const value = canon[type];
      if (!value) continue;
      const idErr = validateIdentifierValue(type, value);
      if (idErr) {
        errors.push(formatImportFieldError(line, type, idErr));
        continue;
      }
      const key = `${type}:${value.toLowerCase()}`;
      if (fileTradeIds.has(key)) errors.push(formatImportFieldError(line, type, `duplicate in file (also row ${fileTradeIds.get(key)})`));
      else fileTradeIds.set(key, line);
    }

    if (!name) errors.push(formatImportFieldError(line, "name", "Missing product name"));

    const attributes: Record<string, string> = {};
    for (const { name: an, idx } of attributeHeaderIndices) {
      const av = cell(r, idx);
      if (av) attributes[normalizeImportAttributeName(an)] = av;
    }

    const supplierName = cell(r, supplierNameIdx);
    const supplierSku = cell(r, supplierSkuIdx);
    const supplierCost = cell(r, supplierCostIdx);
    const supplierMoq = cell(r, supplierMoqIdx);
    const supplierMultiple = cell(r, supplierMultipleIdx);
    const supplierLead = cell(r, supplierLeadIdx);
    const supplierPreferred = cell(r, supplierPreferredIdx).toLowerCase() === "true";
    const __supplier = supplierName
      ? {
          supplier_name: supplierName,
          supplier_sku: supplierSku || null,
          supplier_cost: supplierCost && isNum(supplierCost) ? Number(supplierCost) : null,
          min_order_qty: supplierMoq && isNum(supplierMoq) ? Number(supplierMoq) : 1,
          order_multiple: supplierMultiple && isNum(supplierMultiple) ? Number(supplierMultiple) : null,
          lead_time_days: supplierLead && isNum(supplierLead) ? Number(supplierLead) : null,
          is_preferred: supplierPreferred,
        }
      : null;

    const skuKey = sku.toLowerCase();
    const barKey = barcode.toLowerCase();
    const refKey = reference.trim().toLowerCase();
    if (sku) fileProductSkus.add(skuKey);

    if (sku) {
      if (fileSku.has(skuKey)) errors.push(`Duplicate SKU in file (also row ${fileSku.get(skuKey)})`);
      else fileSku.set(skuKey, line);
    }
    if (barcode) {
      if (fileBarcode.has(barKey)) errors.push(`Duplicate barcode in file (also row ${fileBarcode.get(barKey)})`);
      else fileBarcode.set(barKey, line);
    }
    if (reference) {
      if (fileReference.has(refKey)) errors.push(`Duplicate reference in file (also row ${fileReference.get(refKey)})`);
      else fileReference.set(refKey, line);
    }
    if (cost && !isNum(cost)) errors.push("cost_price must be a number");
    if (price && !isNum(price)) errors.push("selling_price must be a number");
    if (rop && !isNum(rop)) errors.push("reorder_point must be a number");
    if (roq && !isNum(roq)) errors.push("reorder_qty must be a number");

    let categoryId: string | null = null;
    if (category) {
      const key = category.toLowerCase();
      if (catByName.has(key)) categoryId = catByName.get(key)!;
      else warnings.push(`Unknown category "${category}" — left uncategorised`);
    }

    const bySku = sku ? productBySku.get(skuKey) : undefined;
    const byBarcode = barcode ? productByBarcode.get(barKey) : undefined;
    const byReference = reference ? productByReference.get(refKey) : undefined;
    let action: RowAction = "create";
    let updateId: string | undefined;
    let matchNote: string | undefined;
    const conflicts: string[] = [];

    const matchedIds = new Set([bySku?.id, byBarcode?.id, byReference?.id].filter((id): id is string => !!id));
    if (matchedIds.size > 1) {
      conflicts.push("Identifiers match more than one existing product — resolve manually");
    } else if (bySku) {
      action = "update";
      updateId = bySku.id;
      matchNote = "matches existing SKU";
    } else if (byBarcode) {
      action = "update";
      updateId = byBarcode.id;
      matchNote = "matches existing barcode";
    } else if (byReference) {
      action = "update";
      updateId = byReference.id;
      matchNote = "matches existing reference";
    } else if (sku && variantSkuSet.has(skuKey)) {
      warnings.push("SKU is already used by a variant");
    } else if (barcode && variantBarcodeSet.has(barKey)) {
      warnings.push("Barcode is already used by a variant");
    } else if (!sku && !barcode && !reference) {
      warnings.push("No SKU, barcode or reference — cannot be matched on re-import and may duplicate");
    }

    if (updateId || action === "create") {
      for (const type of ["gtin", "upc", "ean"] as const) {
        const value = canon[type];
        if (!value) continue;
        const owner = identRows.find((row) => row.type.toLowerCase() === type && row.value.trim().toLowerCase() === value.toLowerCase());
        if (!owner) continue;
        if (action === "create") {
          errors.push(formatImportFieldError(line, type, "already belongs to another product/variant — not moved"));
        } else if (owner.product_id !== updateId || owner.variant_id) {
          errors.push(formatImportFieldError(line, type, "already belongs to another product/variant — not moved"));
        }
      }
    }

    parsed.push({
      sourceLine: line,
      label: name || sku || `Row ${line}`,
      action: conflicts.length > 0 || errors.length ? "skip" : action,
      updateId,
      payload: {
        name,
        reference: reference || null,
        sku: sku || null,
        barcode: barcode || null,
        category_id: categoryId,
        description: desc || null,
        cost_price: cost ? Number(cost) : null,
        selling_price: price ? Number(price) : null,
        reorder_point: rop ? Number(rop) : 0,
        reorder_qty: roq ? Number(roq) : 0,
        status: action === "update" ? undefined : "active",
        brand: canon.brand,
        manufacturer: canon.manufacturer,
        mpn: canon.mpn,
        condition: canon.condition,
        country_of_origin: canon.country_of_origin,
        weight_kg: canon.weight_kg,
        length_cm: canon.length_cm,
        width_cm: canon.width_cm,
        height_cm: canon.height_cm,
        identifiers_not_applicable: canon.identifiers_not_applicable,
        gtin: canon.gtin,
        upc: canon.upc,
        ean: canon.ean,
        __kind: "product",
        __attributes: attributes,
        __supplier,
        __touched: {
          brand: Boolean(cell(r, headerIndex(headers, "brand"))),
          manufacturer: Boolean(cell(r, headerIndex(headers, "manufacturer"))),
          mpn: Boolean(cell(r, headerIndex(headers, "mpn"))),
          condition: Boolean(cell(r, headerIndex(headers, "condition"))),
          country_of_origin: Boolean(cell(r, headerIndex(headers, "country_of_origin"))),
          weight_kg: Boolean(cell(r, headerIndex(headers, "weight_kg")) || cell(r, headerIndex(headers, "package_weight_kg"))),
          length_cm: Boolean(cell(r, headerIndex(headers, "length_cm")) || cell(r, headerIndex(headers, "package_length_cm"))),
          width_cm: Boolean(cell(r, headerIndex(headers, "width_cm")) || cell(r, headerIndex(headers, "package_width_cm"))),
          height_cm: Boolean(cell(r, headerIndex(headers, "height_cm")) || cell(r, headerIndex(headers, "package_height_cm"))),
          identifiers_not_applicable: Boolean(cell(r, headerIndex(headers, "identifiers_not_applicable"))),
          gtin: Boolean(cell(r, headerIndex(headers, "gtin"))),
          upc: Boolean(cell(r, headerIndex(headers, "upc"))),
          ean: Boolean(cell(r, headerIndex(headers, "ean"))),
          cost_price: Boolean(cost),
          selling_price: Boolean(price),
          description: Boolean(desc),
          barcode: Boolean(barcode),
          reference: Boolean(reference),
          reorder_point: Boolean(rop),
          reorder_qty: Boolean(roq),
          category_id: Boolean(category),
          sku: Boolean(sku),
        },
      },
      warnings,
      errors,
      conflicts,
      matchNote,
    });
  }

  // Variant rows (optional): only create under an existing parent product.
  const hasVariantCols = vskuIdx >= 0 || voptNameIdx >= 0 || voptValIdx >= 0 || optionHeaderIndices.length > 0;
  if (hasVariantCols) {
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      const vsku = cell(r, vskuIdx);
      const vbar = cell(r, vbarIdx);
      if (!vsku && !vbar) continue;

      const parentSku = cell(r, skuIdx).toLowerCase();
      const vcost = cell(r, vcostIdx);
      const vprice = cell(r, vpriceIdx);

      const vgtin = cell(r, vgtinIdx);
      const vupc = cell(r, vupcIdx);
      const vean = cell(r, veanIdx);

      const errors: string[] = [];
      const warnings: string[] = [];

      const inDb = parentSku ? productBySku.get(parentSku) : undefined;
      const inFile = parentSku ? fileProductSkus.has(parentSku) : false;
      if (!inDb && !inFile) errors.push(formatImportFieldError(i + 1, "variant_sku", "parent SKU does not match an existing or imported product"));

      let variantAction: RowAction = "create";
      let variantUpdateId: string | undefined;
      let variantMatch: string | undefined;

      if (!vsku) errors.push(formatImportFieldError(i + 1, "variant_sku", "required for variant rows"));
      else {
        const vk = vsku.toLowerCase();
        if (fileVariantSku.has(vk)) errors.push(formatImportFieldError(i + 1, "variant_sku", `duplicate in file (also row ${fileVariantSku.get(vk)})`));
        else fileVariantSku.set(vk, i + 1);
        const existingVar = variantBySku.get(vk);
        if (existingVar) {
          if (inDb && existingVar.product_id !== inDb.id) {
            errors.push(formatImportFieldError(i + 1, "variant_sku", "already exists on another product"));
          } else {
            variantAction = "update";
            variantUpdateId = existingVar.id;
            variantMatch = "matches existing variant SKU";
          }
        }
      }
      if (vbar) {
        const vbk = vbar.toLowerCase();
        if (fileVariantBarcode.has(vbk)) errors.push(formatImportFieldError(i + 1, "variant_barcode", `duplicate in file (also row ${fileVariantBarcode.get(vbk)})`));
        else fileVariantBarcode.set(vbk, i + 1);
        if (variantBarcodeSet.has(vbk) && variantAction !== "update") errors.push(formatImportFieldError(i + 1, "variant_barcode", "already exists"));
      }
      if (vcost && !isNum(vcost)) errors.push(formatImportFieldError(i + 1, "variant_cost", "must be a number"));
      if (vprice && !isNum(vprice)) errors.push(formatImportFieldError(i + 1, "variant_price", "must be a number"));

      const variantIdents: { type: "gtin" | "upc" | "ean"; value: string; field: string }[] = [];
      if (vgtin) variantIdents.push({ type: "gtin", value: vgtin, field: "variant_gtin" });
      if (vupc) variantIdents.push({ type: "upc", value: vupc, field: "variant_upc" });
      if (vean) variantIdents.push({ type: "ean", value: vean, field: "variant_ean" });
      for (const ident of variantIdents) {
        const idErr = validateIdentifierValue(ident.type, ident.value);
        if (idErr) {
          errors.push(formatImportFieldError(i + 1, ident.field, idErr));
          continue;
        }
        const key = `${ident.type}:${ident.value.toLowerCase()}`;
        if (fileTradeIds.has(key)) errors.push(formatImportFieldError(i + 1, ident.field, `duplicate in file (also row ${fileTradeIds.get(key)})`));
        else fileTradeIds.set(key, i + 1);
        const owner = identRows.find((row) => row.type.toLowerCase() === ident.type && row.value.trim().toLowerCase() === ident.value.toLowerCase());
        if (owner) {
          const sameVariant = variantUpdateId && owner.variant_id === variantUpdateId;
          if (!sameVariant) errors.push(formatImportFieldError(i + 1, ident.field, "already belongs to another product/variant — not moved"));
        }
      }

      // Build option_values: V2 multi-option OR legacy single-option
      const optionValues: Record<string, string> = {};
      if (optionHeaderIndices.length > 0) {
        for (const { name, idx } of optionHeaderIndices) {
          const v = cell(r, idx);
          if (v) optionValues[name] = v;
        }
      } else {
        const optName = cell(r, voptNameIdx);
        const optVal = cell(r, voptValIdx);
        if (optName && optVal) optionValues[optName] = optVal;
      }

      parsed.push({
        sourceLine: i + 1,
        label: `${vsku || Object.values(optionValues).join("/") || "variant"} (of ${parentSku || "?"})`,
        action: errors.length ? "skip" : variantAction,
        updateId: variantUpdateId,
        payload: {
          __kind: "variant",
          parent_id: inDb?.id ?? null,
          parent_sku: parentSku || null,
          sku: vsku || null,
          barcode: vbar || null,
          option_values: optionValues,
          cost_price: vcost ? Number(vcost) : null,
          selling_price: vprice ? Number(vprice) : null,
          variant_gtin: vgtin || null,
          variant_upc: vupc || null,
          variant_ean: vean || null,
        },
        warnings,
        errors,
        conflicts: [],
        matchNote: variantMatch,
      });
    }
  }

  return parsed;
}

// ---------------------------------------------------------------------------
// Customers — deterministic duplicate matching by email (strong) then name.
// ---------------------------------------------------------------------------
export async function parseCustomersCsv(rows: string[][], headers: string[], orgId: string): Promise<ParsedRow[]> {
  const nameIdx = headerIndex(headers, "name");
  const emailIdx = headerIndex(headers, "email");
  const phoneIdx = headerIndex(headers, "phone");

  const { data } = await supabase.from("customers").select("id, name, email").eq("org_id", orgId);
  const byEmail = new Map<string, { id: string }>();
  const byName = new Map<string, { id: string }>();
  (data ?? []).forEach((c: { id: string; name: string; email: string | null }) => {
    if (c.email) byEmail.set(c.email.trim().toLowerCase(), { id: c.id });
    byName.set(c.name.trim().toLowerCase(), { id: c.id });
  });

  const parsed: ParsedRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const name = cell(r, nameIdx);
    const email = cell(r, emailIdx);
    const phone = cell(r, phoneIdx);

    const errors: string[] = [];
    const warnings: string[] = [];
    if (!name) errors.push("Missing customer name");

    let action: RowAction = "create";
    let updateId: string | undefined;
    let matchNote: string | undefined;

    if (email) {
      const m = byEmail.get(email.toLowerCase());
      if (m) {
        action = "update";
        updateId = m.id;
        matchNote = "matches existing email";
      }
    } else {
      const m = byName.get(name.toLowerCase());
      if (m) {
        action = "skip";
        warnings.push("Name matches an existing customer but no email was provided — requires manual resolution");
        matchNote = "possible duplicate (name only)";
      }
    }

    parsed.push({
      sourceLine: i + 1,
      label: name || `Row ${i + 1}`,
      action: errors.length ? "skip" : action,
      updateId,
      payload: { name, email: email || null, phone: phone || null },
      warnings,
      errors,
      conflicts: [],
      matchNote,
    });
  }
  return parsed;
}

// ---------------------------------------------------------------------------
// Suppliers — deterministic duplicate matching by code, then email, then name.
// ---------------------------------------------------------------------------
export async function parseSuppliersCsv(rows: string[][], headers: string[], orgId: string): Promise<ParsedRow[]> {
  const nameIdx = headerIndex(headers, "name");
  const codeIdx = headerIndex(headers, "code");
  const contactIdx = headerIndex(headers, "contact_name");
  const emailIdx = headerIndex(headers, "email");
  const phoneIdx = headerIndex(headers, "phone");
  const cityIdx = headerIndex(headers, "city");
  const countryIdx = headerIndex(headers, "country");
  const termsIdx = headerIndex(headers, "payment_terms");
  const leadIdx = headerIndex(headers, "lead_time_days");

  const { data } = await supabase.from("suppliers").select("id, name, code, email").eq("org_id", orgId);
  const byCode = new Map<string, { id: string }>();
  const byEmail = new Map<string, { id: string }>();
  const byName = new Map<string, { id: string }>();
  (data ?? []).forEach((s: { id: string; name: string; code: string | null; email: string | null }) => {
    if (s.code) byCode.set(s.code.trim().toLowerCase(), { id: s.id });
    if (s.email) byEmail.set(s.email.trim().toLowerCase(), { id: s.id });
    byName.set(s.name.trim().toLowerCase(), { id: s.id });
  });

  const parsed: ParsedRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const name = cell(r, nameIdx);
    const code = cell(r, codeIdx);
    const email = cell(r, emailIdx);
    const lead = cell(r, leadIdx);

    const errors: string[] = [];
    const warnings: string[] = [];
    if (!name) errors.push("Missing supplier name");
    if (lead && !isNum(lead)) errors.push("lead_time_days must be a number");

    let action: RowAction = "create";
    let updateId: string | undefined;
    let matchNote: string | undefined;

    if (code) {
      const m = byCode.get(code.toLowerCase());
      if (m) {
        action = "update";
        updateId = m.id;
        matchNote = "matches existing code";
      }
    } else if (email) {
      const m = byEmail.get(email.toLowerCase());
      if (m) {
        action = "update";
        updateId = m.id;
        matchNote = "matches existing email";
      }
    } else {
      const m = byName.get(name.toLowerCase());
      if (m) {
        action = "skip";
        warnings.push("Name matches an existing supplier but no code/email was provided — requires manual resolution");
        matchNote = "possible duplicate (name only)";
      }
    }

    parsed.push({
      sourceLine: i + 1,
      label: name || `Row ${i + 1}`,
      action: errors.length ? "skip" : action,
      updateId,
      payload: {
        name,
        code: code || null,
        contact_name: cell(r, contactIdx) || null,
        email: email || null,
        phone: cell(r, phoneIdx) || null,
        city: cell(r, cityIdx) || null,
        country: cell(r, countryIdx) || null,
        payment_terms: cell(r, termsIdx) || null,
        lead_time_days: lead ? Number(lead) : null,
        currency: getActiveCurrencyCode(),
      },
      warnings,
      errors,
      conflicts: [],
      matchNote,
    });
  }
  return parsed;
}

// ---------------------------------------------------------------------------
// Full product export: round-trips product fields, variants, options (Option:*),
// attributes (Attribute:*), identifiers, prices, weights and dimensions.
// ---------------------------------------------------------------------------
export async function exportProductsFullCsv(orgId: string): Promise<string> {
  const [prodRes, varRes, optRes, attrRes, attrValRes, idRes, catRes, supRes, suppliersRes] = await Promise.all([
    supabase.from("products").select("id, name, reference, sku, brand, manufacturer, mpn, condition, country_of_origin, identifiers_not_applicable, description, category_id, cost_price, selling_price, reorder_point, reorder_qty, weight_kg, length_cm, width_cm, height_cm, status, product_type").eq("org_id", orgId).order("name"),
    supabase.from("variants").select("id, product_id, sku, barcode, option_values, cost_price, selling_price, weight_kg, length_cm, width_cm, height_cm, status").eq("org_id", orgId).order("product_id, position"),
    supabase.from("product_options").select("id, product_id, name, position").eq("org_id", orgId).eq("status", "active").order("position"),
    supabase.from("product_attributes").select("id, name").eq("org_id", orgId).order("position"),
    supabase.from("product_attribute_values").select("product_id, attribute_id, value").eq("org_id", orgId),
    supabase.from("product_identifiers").select("product_id, variant_id, type, value").eq("org_id", orgId),
    supabase.from("categories").select("id, name").eq("org_id", orgId),
    supabase.from("supplier_products").select("product_id, supplier_id, variant_id, supplier_sku, supplier_price, min_order_qty, order_multiple, lead_time_days, is_preferred").eq("org_id", orgId).is("variant_id", null),
    supabase.from("suppliers").select("id, name, code").eq("org_id", orgId),
  ]);

  const categories = new Map<string, string>();
  (catRes.data ?? []).forEach((c: { id: string; name: string }) => categories.set(c.id, c.name));

  const supplierNames = new Map<string, string>();
  (suppliersRes.data ?? []).forEach((s: { id: string; name: string; code: string | null }) => supplierNames.set(s.id, s.name));

  // Preferred (or first) product-level supplier link per product.
  const supplierByProduct = new Map<string, { supplier_id: string; supplier_sku: string | null; supplier_price: number | null; min_order_qty: number; order_multiple: number | null; lead_time_days: number | null; is_preferred: boolean }>();
  (supRes.data ?? []).forEach((sp: { product_id: string; supplier_id: string; supplier_sku: string | null; supplier_price: number | null; min_order_qty: number; order_multiple: number | null; lead_time_days: number | null; is_preferred: boolean }) => {
    const pid = sp.product_id;
    const existing = supplierByProduct.get(pid);
    if (!existing || (sp.is_preferred && !existing.is_preferred)) supplierByProduct.set(pid, sp);
  });

  const attributeNames = new Map<string, string>();
  (attrRes.data ?? []).forEach((a: { id: string; name: string }) => attributeNames.set(a.id, a.name));

  const attrValuesByProduct = new Map<string, Record<string, string>>();
  (attrValRes.data ?? []).forEach((av: { product_id: string; attribute_id: string; value: string | null }) => {
    const name = attributeNames.get(av.attribute_id) ?? av.attribute_id;
    if (!attrValuesByProduct.has(av.product_id)) attrValuesByProduct.set(av.product_id, {});
    if (av.value != null) attrValuesByProduct.get(av.product_id)![name] = av.value;
  });

  const prodIdents = new Map<string, Record<string, string>>();
  const varIdents = new Map<string, Record<string, string>>();
  (idRes.data ?? []).forEach((idn: { product_id: string | null; variant_id: string | null; type: string; value: string }) => {
    if (idn.product_id && !idn.variant_id) {
      if (!prodIdents.has(idn.product_id)) prodIdents.set(idn.product_id, {});
      prodIdents.get(idn.product_id)![idn.type] = idn.value;
    } else if (idn.variant_id) {
      if (!varIdents.has(idn.variant_id)) varIdents.set(idn.variant_id, {});
      varIdents.get(idn.variant_id)![idn.type] = idn.value;
    }
  });

  const optionNames = Array.from(new Set((optRes.data ?? []).map((o: { name: string }) => o.name)));
  const attrNames = Array.from(new Set((attrRes.data ?? []).map((a: { name: string }) => a.name)));
  const headers = [
    "name", "reference", "sku", "brand", "manufacturer", "mpn", "condition", "country_of_origin",
    "category", "cost_price", "selling_price", "reorder_point", "reorder_qty",
    "weight_kg", "length_cm", "width_cm", "height_cm", "status", "product_type", "description",
    "identifiers_not_applicable", "gtin", "upc", "ean",
    "variant_sku", "variant_barcode", "variant_gtin", "variant_upc", "variant_ean", "variant_cost", "variant_price",
    ...optionNames.map((n) => `Option:${n}`),
    ...attrNames.map((n) => `Attribute:${n}`),
    "supplier_name", "supplier_sku", "supplier_cost", "supplier_moq", "supplier_order_multiple", "supplier_lead_time", "supplier_preferred",
  ];

  const rows: (string | number | null)[][] = [];
  const products = (prodRes.data ?? []) as Array<Record<string, unknown>>;
  const variants = (varRes.data ?? []) as Array<Record<string, unknown>>;
  const variantsByProduct = new Map<string, Array<Record<string, unknown>>>();
  variants.forEach((v) => {
    const pid = String(v.product_id);
    if (!variantsByProduct.has(pid)) variantsByProduct.set(pid, []);
    variantsByProduct.get(pid)!.push(v);
  });

  for (const p of products) {
    const pid = String(p.id);
    const pv = variantsByProduct.get(pid) ?? [];
    const catName = p.category_id ? categories.get(String(p.category_id)) ?? "" : "";
    const attrs = attrValuesByProduct.get(pid) ?? {};
    const pIdents = prodIdents.get(pid) ?? {};
    const emit = (v: Record<string, unknown> | null) => {
      const vIdents = v ? varIdents.get(String(v.id)) ?? {} : {};
      const optionVals = (v?.option_values as Record<string, string> | null) ?? {};
      const sp = supplierByProduct.get(pid);
      rows.push([
        String(p.name ?? ""), String(p.reference ?? ""), String(p.sku ?? ""),
        String(p.brand ?? ""), String(p.manufacturer ?? ""), String(p.mpn ?? ""), String(p.condition ?? ""), String(p.country_of_origin ?? ""),
        catName,
        p.cost_price as number | null, p.selling_price as number | null, p.reorder_point as number, p.reorder_qty as number,
        p.weight_kg as number | null, p.length_cm as number | null, p.width_cm as number | null, p.height_cm as number | null,
        String(p.status ?? ""), String(p.product_type ?? ""), String(p.description ?? ""),
        p.identifiers_not_applicable ? "true" : "",
        pIdents.gtin ?? "", pIdents.upc ?? "", pIdents.ean ?? "",
        v ? String(v.sku ?? "") : "", v ? String(v.barcode ?? "") : "",
        v ? (vIdents.gtin ?? "") : "", v ? (vIdents.upc ?? "") : "", v ? (vIdents.ean ?? "") : "",
        v ? (v.cost_price as number | null) : null, v ? (v.selling_price as number | null) : null,
        ...optionNames.map((n) => optionVals[n] ?? ""),
        ...attrNames.map((n) => attrs[n] ?? ""),
        sp ? (supplierNames.get(sp.supplier_id) ?? "") : "",
        sp ? (sp.supplier_sku ?? "") : "",
        sp ? (sp.supplier_price as number | null) : null,
        sp ? sp.min_order_qty : null,
        sp ? (sp.order_multiple as number | null) : null,
        sp ? (sp.lead_time_days as number | null) : null,
        sp ? (sp.is_preferred ? "true" : "false") : "",
      ]);
    };
    if (pv.length === 0) emit(null);
    else pv.forEach((v) => emit(v));
  }

  return toCsv(headers, rows);
}